// @ts-strict-ignore
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import analytics from '../../../modules/analytics';
import { getAndroidVersion, getIOSVersion } from '../../../modules/AppInfo';
import { AuthOrigins } from '../../../modules/user/constants';
import { openExternalUrl } from '../../../modules/openExternalUrl';
import Sidebar from './Sidebar';
import { LegalDoc, LegalModal } from '../legal/LegalModal';
import {
  CALENDAR_ROUTE,
  CLIENT_ACCOUNT_SETTINGS_ROUTE,
  CLIENT_APPTS_LIST,
  HOME_ROUTE,
  PRO_TODAY_TAB_HOME,
  SEARCH_ROUTE,
} from '../../../route-names';
import type { State as CurrentUserState } from '../../../store/CurrentUser.types';
import { useLinkTo, useIsMobile } from '../../../hooks';
import { selectors } from '../../../store/CurrentUser.model';
import type { RootState } from '../../../store/models';
import { SearchPopoverModal } from '../../consumer/search/SearchPopover';
import { SITE_ROOT } from '../../../config';
import { addQueryStringParams } from '../../../modules/url';
import { SUPPORT_PAGE_URL } from '../../../modules/SupportServiceWrapper';
import { ABTestMenuModal } from '../developer/ABTestMenu';
import nonCriticalException from '../../../modules/exceptionLogger';
import { useShowSubscriptionFeatures } from '../../../hooks/useShowSubscriptionFeatures';
import { useTriggerLoginModal } from '../login/LoginModalProvider/useTriggerLoginModal';
import { LoginModalMode } from '../login/LoginModalProvider/LoginModalProvider';

interface SidebarContainerProps {
  isSidebarDisplayed: boolean;
  handleClose: () => void;
}

const SidebarContainer: React.FC<SidebarContainerProps> = ({
  isSidebarDisplayed,
  handleClose,
}) => {
  const linkTo = useLinkTo();
  const utmParams = { source: 'hamburger_menu' };
  const isMobile = useIsMobile();
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const isProvider = useSelector(selectors.isProvider);
  const searchModel = useSelector<RootState, RootState['search']>(state => state.search);
  const [displaySearchModal, setDisplaySearchModal] = useState<boolean>(false);
  const [docLegalModal, setDocLegalModal] = useState<LegalDoc>();
  const [isDeveloperMenuOpen, setIsDeveloperMenuOpen] = useState<boolean>(false);
  const user = useSelector<RootState, CurrentUserState>(state => state.user);
  const isApp = (!!getIOSVersion() || getAndroidVersion()) && window.appAvailability;
  const [canShowSubscriptionFeatures] = useShowSubscriptionFeatures();
  const logUserIn = useTriggerLoginModal();

  const handleSocialClick = (event: React.MouseEvent, network: string) => {
    event.preventDefault();

    const isIOS = !!getIOSVersion();
    let fallbackURL;
    let appURL;
    let appID;
    let channel;

    switch (network) {
      case 'facebook':
        fallbackURL = 'https://www.facebook.com/484439085178';
        appURL = isIOS ? 'https://www.facebook.com/484439085178' : 'fb://page/484439085178';
        appID = isIOS ? 'fb://' : 'com.facebook.katana';
        channel = network;
        break;
      case 'instagram':
        fallbackURL = 'https://instagram.com/styleseat/';
        appURL = 'instagram://user?username=styleseat';
        appID = isIOS ? 'instagram://' : 'com.instagram.android';
        channel = network;
        break;
      default:
        fallbackURL = 'https://twitter.com/styleseat';
        appURL = 'twitter://user?screen_name=styleseat';
        appID = isIOS ? 'twitter://' : 'com.twitter.android';
        channel = network;
        break;
    }

    analytics.track('C Clicked Follow SS', {
      follow_channel: channel,
      follow_source: 'sidebar',
    });

    if (isApp) {
      window.appAvailability.check(
        appID,
        () => {
          window.open(appURL, '_system', 'location=yes');
        },
        () => {
          openExternalUrl(fallbackURL, '_system', 'location=yes');
        },
      );
    } else {
      openExternalUrl(fallbackURL, '_blank');
    }
    handleClose();
  };

  const handleLogin = async () => {
    handleClose();

    try {
      const { userData } = await logUserIn({
        mode: LoginModalMode.Login,
        origin: AuthOrigins.HeaderNav,
        useNewExperience: true,
        loginRequired: false,
      });
      if (userData?.provider_id) {
        linkTo(PRO_TODAY_TAB_HOME, { providerId: userData.provider_id });
      }
    } catch (e) {
      nonCriticalException(e);
    }
  };

  const onSearch = () => {
    handleClose();
    if (isMobile) {
      setDisplaySearchModal(true);
    } else {
      linkTo(SEARCH_ROUTE);
    }
  };

  const goToExternalPage = (route = '', target = '_self') => {
    openExternalUrl(
      addQueryStringParams(`${SITE_ROOT}/${route}`, { source: 'hamburger_menu', ...utmParams }),
      isApp ? '_system' : target,
    );
  };

  const handleSwitchToBusiness = () => {
    const params = {
      providerId: user.providerId,
    };
    if (isMobile) {
      linkTo(PRO_TODAY_TAB_HOME, params);
    } else {
      linkTo(CALENDAR_ROUTE, params);
    }
  };

  const onSignUp = async () => {
    handleClose();

    try {
      const { userData } = await logUserIn({
        mode: LoginModalMode.Signup,
        origin: AuthOrigins.HeaderNav,
        useNewExperience: true,
        loginRequired: false,
      });

      if (userData?.provider_id) {
        linkTo(PRO_TODAY_TAB_HOME, { providerId: userData.provider_id });
      }
    } catch (e) {
      nonCriticalException(e);
    }
  };

  const handleGoToHome = () => {
    handleClose();
    linkTo(HOME_ROUTE);
  };

  const handleGoToMyAccount = () => {
    handleClose();
    linkTo(CLIENT_ACCOUNT_SETTINGS_ROUTE);
  };

  const handleGoToMyAppointemnts = () => {
    handleClose();
    linkTo(CLIENT_APPTS_LIST);
  };

  const renderSidebar = () => {
    analytics.track('StyleSeat HomePage Hamburger Viewed', {
      loggedIn: isLoggedIn,
      isProvider,
      isClient: isLoggedIn && !isProvider,
    });
    return (
      <Sidebar
        linkTo={linkTo}
        isLoggedIn={isLoggedIn}
        isProvider={isProvider}
        user={user}
        utmParams={utmParams}
        onClose={handleClose}
        onLogin={handleLogin}
        onSocialClick={handleSocialClick}
        openPrivacy={() => setDocLegalModal(LegalDoc.PrivacyPolicyInfo)}
        openTerms={() => setDocLegalModal(LegalDoc.ProTermsOfService)}
        openSearchModal={onSearch}
        switchToBusiness={handleSwitchToBusiness}
        onSignUp={onSignUp}
        goToExternalPage={goToExternalPage}
        openHelpCenter={() => openExternalUrl(SUPPORT_PAGE_URL, isApp ? '_system' : '_self')}
        goToHome={handleGoToHome}
        goToMyAccount={handleGoToMyAccount}
        goToMyAppointments={handleGoToMyAppointemnts}
        displayDeveloperMenu={() => setIsDeveloperMenuOpen(true)}
        showProOnboardingEntryPoints={canShowSubscriptionFeatures}
      />
    );
  };

  return (
    <React.Fragment>
      {isSidebarDisplayed && renderSidebar()}
      {docLegalModal && (
        <LegalModal doc={docLegalModal} onDismiss={() => setDocLegalModal(null)} />
      )}
      {displaySearchModal && (
        <SearchPopoverModal
          query={searchModel.query}
          source="hamburguer_menu"
          onClose={() => setDisplaySearchModal(false)}
        />
      )}
      {isDeveloperMenuOpen && (
        <ABTestMenuModal
          onClose={() => setIsDeveloperMenuOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default SidebarContainer;
